/**
 * キャリコネクション送客で使う保存済み書式（localforage）の読み込み。
 *
 * 職務経歴書は保存先が2つある。通常の職務経歴書は `format_shokumukeirekisho`、
 * スキルシート（skillsheet_type）は `format_skillsheet_type`。両方を持つ会員では
 * 「どちらを送るか」を決める必要がある。書類ごとの最終編集日時は保存されていない
 * （createDate は作成日で Date 型のまま残る）ため、次の順で決める。
 *   1. 出力中の書式がスキルシートならスキルシート、職務経歴書なら職務経歴書
 *   2. 履歴書を出力中なら、最後に編集した書式（recentEdit.FormatId）の側
 *   3. どちらでもなければ作成日の新しい方（Date / 文字列 / 数値を受ける）
 */
import localforage from 'localforage'
import { expectedDocumentTypeForFormat } from '@/utils/format-edit/formatDetailBackup'

type UnknownRecord = Record<string, any>
type GetItem = (key: string) => Promise<unknown>

export type CcShokumuSource = 'shokumukeirekisho' | 'skillsheet' | null

export interface CcStoredFormats {
  formatRirekisho: UnknownRecord | null
  formatShokumukeirekisho: UnknownRecord | null
  /** formatShokumukeirekisho をどちらの保存先から取ったか */
  shokumuSource: CcShokumuSource
}

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

const isSkillsheetFormat = (formatId: string): boolean =>
  formatId.includes('skillsheet')

const isShokumuFormat = (formatId: string): boolean =>
  !!formatId &&
  expectedDocumentTypeForFormat(formatId) === 'shokumukeirekisho' &&
  !isSkillsheetFormat(formatId)

function timeOf(format: UnknownRecord): number {
  for (const key of ['updateDate', 'update_date', 'writeDate', 'createDate']) {
    const raw = format[key]
    const t =
      raw instanceof Date
        ? raw.getTime()
        : typeof raw === 'string' || typeof raw === 'number'
          ? new Date(raw).getTime()
          : NaN
    if (!Number.isNaN(t)) return t
  }
  return 0
}

/** 職務経歴書とスキルシートのどちらを送るか（ファイル冒頭の順） */
export function pickShokumuFormat(
  shokumu: UnknownRecord | null,
  skillsheet: UnknownRecord | null,
  formatId: string,
  recentFormatId: string
): { format: UnknownRecord | null; source: CcShokumuSource } {
  if (!shokumu || !skillsheet) {
    if (shokumu) return { format: shokumu, source: 'shokumukeirekisho' }
    if (skillsheet) return { format: skillsheet, source: 'skillsheet' }
    return { format: null, source: null }
  }
  const byFormat = (id: string): CcShokumuSource =>
    isSkillsheetFormat(id)
      ? 'skillsheet'
      : isShokumuFormat(id)
        ? 'shokumukeirekisho'
        : null
  const source =
    byFormat(formatId) ||
    byFormat(recentFormatId) ||
    (timeOf(skillsheet) > timeOf(shokumu) ? 'skillsheet' : 'shokumukeirekisho')
  return {
    format: source === 'skillsheet' ? skillsheet : shokumu,
    source
  }
}

/** localforage の保存書式を読む（ビズリーチ送客と同じキー。失敗は null） */
export async function loadFormatsFromStorage(
  getItem: GetItem = (key) => localforage.getItem(key),
  formatId = ''
): Promise<CcStoredFormats> {
  const safeGet = async (key: string) => {
    try {
      const v = await getItem(key)
      return isRecord(v) ? v : null
    } catch (_e) {
      return null
    }
  }
  const [formatRirekisho, shokumu, skillsheet, recentEdit] = await Promise.all([
    safeGet('format_rirekisho'),
    safeGet('format_shokumukeirekisho'),
    safeGet('format_skillsheet_type'),
    safeGet('recentEdit')
  ])
  const picked = pickShokumuFormat(
    shokumu,
    skillsheet,
    String(formatId || ''),
    String((recentEdit && recentEdit.FormatId) || '')
  )
  return {
    formatRirekisho,
    formatShokumukeirekisho: picked.format,
    shokumuSource: picked.source
  }
}
